import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import enTranslations from './locales/en.json';

// Lazy load translation resources
const resources = {
  en: () => import('./locales/en.json').then((module) => module.default),
  de: () => import('./locales/de.json').then((module) => module.default),
  es: () => import('./locales/es.json').then((module) => module.default),
  fr: () => import('./locales/fr.json').then((module) => module.default),
  it: () => import('./locales/it.json').then((module) => module.default),
  pt: () => import('./locales/pt.json').then((module) => module.default),
  nl: () => import('./locales/nl.json').then((module) => module.default),
  ja: () => import('./locales/ja.json').then((module) => module.default),
  ko: () => import('./locales/ko.json').then((module) => module.default),
  ar: () => import('./locales/ar.json').then((module) => module.default),
  zh: () => import('./locales/zh.json').then((module) => module.default),
};

const supportedLanguages = ['en', 'de', 'es', 'fr', 'it', 'pt', 'nl', 'ja', 'ko', 'ar', 'zh'];

// RTL languages that need special handling
const rtlLanguages = ['ar'];

// Check if a language is RTL
export const isRTLLanguage = (lang: string): boolean => {
  return rtlLanguages.includes(lang);
};

// Set document direction based on language
export const setDocumentDirection = (lang: string): void => {
  const dir = isRTLLanguage(lang) ? 'rtl' : 'ltr';
  document.documentElement.dir = dir;
  document.documentElement.lang = lang;
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        translation: enTranslations,
      },
    },
    fallbackLng: 'en',
    supportedLngs: supportedLanguages,
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'noox-language',
    },
    
    interpolation: {
      escapeValue: false,
    },
    
    react: {
      useSuspense: false,
    },
  });

// Load initial language resources
const loadInitialResources = async () => {
  const currentLang = i18n.language || 'en';
  const loadResource = resources[currentLang as keyof typeof resources] || resources.en;
  
  // Set document direction on initial load
  setDocumentDirection(currentLang);
  
  try {
    const data = await loadResource();
    i18n.addResourceBundle(currentLang, 'translation', data);
  } catch (error) {
    console.error('Failed to load language resources:', error);
  }
};

// Handle language changes
i18n.on('languageChanged', async (lng) => {
  // Update document direction when language changes
  setDocumentDirection(lng);
  
  const loadResource = resources[lng as keyof typeof resources];
  if (loadResource) {
    try {
      const data = await loadResource();
      i18n.addResourceBundle(lng, 'translation', data, true, true);
    } catch (error) {
      console.error(`Failed to load ${lng} resources:`, error);
    }
  }
});

loadInitialResources();

export default i18n;
